import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../../lib/utils';

export type ChipProps = {
    children: ReactNode;
    size?: 'small' | 'normal' | 'large';
    variant: 'normal' | 'colorful';
    color: 'blue' | 'gray' | 'green' | 'indigo' | 'orange' | 'red' | 'rose' | 'violet' | 'white' | 'yellow';
    bordered?: boolean;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
    className?: string;
};

export const Chip = ({
    variant,
    color,
    bordered = false,
    size = 'normal',
    children,
    dataTestid,
    htmlAttributes,
    className,
}: ChipProps) => {
    if (!children) {
        return null;
    }
    return (
        <div
            className={cn(
                // Common styles
                'font-normal rounded-full inline-block whitespace-nowrap font-body',
                {
                    'text-[11px] leading-4 py-0.5 px-1.5': size === 'small',
                    'text-xs py-1 px-2': size === 'normal',
                    'text-sm py-1.5 px-3': size === 'large',
                },
                // Colorful variants
                {
                    'bg-amber-100 text-amber-700': color === 'yellow' && variant === 'colorful',
                    'bg-sky-100 text-sky-700': color === 'blue' && variant === 'colorful',
                    'bg-emerald-100 text-emerald-700': color === 'green' && variant === 'colorful',
                    'bg-gray-200 text-gray-700': color === 'gray' && variant === 'colorful',
                    'bg-indigo-100 text-indigo-700': color === 'indigo' && variant === 'colorful',
                    'bg-orange-100 text-orange-700': color === 'orange' && variant === 'colorful',
                    'bg-red-100 text-red-700': color === 'red' && variant === 'colorful',
                    'bg-rose-100 text-rose-700': color === 'rose' && variant === 'colorful',
                    'bg-violet-100 text-violet-700': color === 'violet' && variant === 'colorful',
                    'bg-white text-gray-700': color === 'white' && variant === 'colorful',
                },
                // Normal variants
                {
                    'bg-amber-100 text-gray-800': color === 'yellow' && variant === 'normal',
                    'bg-emerald-100 text-gray-800': color === 'green' && variant === 'normal',
                    'bg-gray-100 text-gray-700': color === 'gray' && variant === 'normal',
                    'bg-indigo-100 text-gray-800': color === 'indigo' && variant === 'normal',
                    'bg-orange-100 text-gray-800': color === 'orange' && variant === 'normal',
                    'bg-red-100 text-gray-800': color === 'red' && variant === 'normal',
                    'bg-rose-100 text-gray-800': color === 'rose' && variant === 'normal',
                    'bg-sky-100 text-gray-800': color === 'blue' && variant === 'normal',
                    'bg-violet-100 text-gray-800': color === 'violet' && variant === 'normal',
                    'bg-white text-gray-800': color === 'violet' && variant === 'normal',
                },
                {
                    'border-amber-300 border border-solid': color === 'yellow' && bordered,
                    'border-emerald-300 border border-solid': color === 'green' && bordered,
                    'border-gray-300 border border-solid': color === 'gray' && bordered,
                    'border-indigo-300 border border-solid': color === 'indigo' && bordered,
                    'border-orange-300 border border-solid': color === 'orange' && bordered,
                    'border-red-300 border border-solid': color === 'red' && bordered,
                    'border-rose-300 border border-solid': color === 'rose' && bordered,
                    'border-sky-300 border border-solid': color === 'blue' && bordered,
                    'border-violet-300 border border-solid': color === 'violet' && bordered,
                    'border-gray-200 border border-solid': color === 'white' && bordered,
                },
                className
            )}
            data-testid={dataTestid}
            data-component-name='Chip'
            {...htmlAttributes}
        >
            {children}
        </div>
    );
};
